import { withLayouts } from 'react-dx'
import { Trans, useLingui } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import DefaultLayout from '../_layout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import {
  EmailLink,
  PrivacyPolicyLinks,
  RefundPolicyLinks,
  SiteLink,
} from '../privacy/_components/ArticleLink'
import { COMPANY_NAME } from '../_constant/biz'

import SiteSpecificContextLayout from '@/pages/tad.ai/_layout/layouts/SiteSpecificContextLayout'
import { getServerSideData } from '@/helpers/server-data'
import { useIsAppQuery } from '@/hooks/useIsAppQuery'

const Page = () => {
  const { t } = useLingui()
  return (
    <div
      className='
      mx-auto max-w-[1092px] px-4
      pb-12 lg:pb-24 
      [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h2]:md:text-2xl 
      [&_h3]:my-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_h3]:md:text-lg 
      [&_h4]:my-3 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-white [&_h4]:md:text-base 
      [&_li]:mb-2 [&_li]:md:text-sm 
      [&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:text-sm [&_ol]:text-white [&_ol]:opacity-85
      [&_p]:mb-2 [&_p]:text-sm [&_p]:text-white [&_p]:opacity-85 [&_p]:md:text-sm 
      [&_ul]:list-disc [&_ul]:ps-5 [&_ul]:text-sm [&_ul]:text-white [&_ul]:opacity-85
      '
    >
      <h1 className='text-f-primary mb-8 mt-12 text-center text-[24px] font-bold leading-[36px] md:mt-20 md:text-[56px] md:leading-[72px]'>{t`Terms of Use`}</h1>
      <div className='rounded-3xl bg-black shadow-md lg:bg-[#171717] lg:p-16 lg:px-20'>
        <p>{t`Last updated: December 18, 2025`}</p>

        <p>
          <Trans>
            Welcome to Tad AI ("We," "Us," "Our", or "Service")! By accessing or
            using our services, you acknowledge and agree to the following:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              You are legally capable of entering into a binding contract with
              us.
            </Trans>
          </li>
          <li>
            <Trans>
              You accept and comply with our Terms of Service and Privacy Policy
              (found at
              <PrivacyPolicyLinks />
              ).
            </Trans>
          </li>
          <li>
            <Trans>
              You will adhere to any additional terms and conditions that we may
              publish and update periodically in connection with your use of our
              services.
            </Trans>
          </li>
        </ul>
        <h2>
          <Trans>General Terms and Conditions</Trans>{' '}
        </h2>
        <p>
          <Trans>
            (a) <strong>Consideration</strong>: Different pricing and payment
            policies apply to various services. You may access the website, app,
            or services either for free or for a price displayed on the payment
            page. By using the service for free or with credits, you acknowledge
            that we may generate revenue or goodwill from your usage, and you
            have no rights to share that revenue.
          </Trans>
        </p>
        <p>
          <Trans>
            (b) <strong>Changes to this Agreement</strong>: We may modify this
            Agreement at any time without prior notice but will attempt to
            notify you of significant changes. You can view the current
            Agreement via the service. Continued use after changes indicates
            acceptance of the revised Agreement. If you disagree with any
            changes, your only option is to stop using the service. This
            Agreement governs any updates unless accompanied by a separate
            license or agreement, which will then apply. Changes do not affect
            disputes arising before the revisions.
          </Trans>
        </p>
        <p>
          <Trans>
            (c) <strong>Privacy Policy</strong>: Access and use of the service
            are subject to Tad AI's Privacy Policy, which is incorporated by
            reference.
          </Trans>
        </p>
        <p>
          <Trans>
            (d) <strong>Eligibility</strong>: The service is not available to
            users previously suspended by Tad AI. In the U.S., users must be at
            least 13 years old (18 for paid services) and must have parental
            consent if under 13. Other regions will require parental consent
            based on local laws. Different age restrictions may apply based on
            distribution platforms.
          </Trans>
        </p>
        <h2>
          <Trans>Account Security</Trans>
        </h2>
        <p>
          <Trans>
            You are responsible for maintaining the security of your account and
            all activities that occur using your login credentials. If you
            suspect any unauthorized use of your account or credentials, you
            agree to notify Tad immediately via <EmailLink />.
          </Trans>
          &nbsp;
        </p>
        <p>
          <Trans>
            Tad AI is not liable for any loss or damage arising from
            unauthorized account use prior to receiving such notification.
          </Trans>
          &nbsp;
        </p>
        <h2>
          <Trans>User Content Rights</Trans>
        </h2>
        <p>
          <Trans>
            Users of Tad AI's services should be aware of the following
            intellectual property rights regarding AI-generated music:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              <strong>Free Plan Users</strong>: AI-generated music created by
              free users of Tad AI cannot be used for commercial purposes. This
              means you may enjoy the music for personal use, but you cannot
              sell, distribute, or profit from it in any way.
            </Trans>
          </li>
          <li>
            <Trans>
              <strong>Paid Plan Users</strong>: If you are a paid user, you are
              granted the right to use AI-generated music for commercial
              purposes. This includes the ability to sell, distribute, or
              otherwise monetize the music created through our services.
            </Trans>
          </li>
        </ul>
        <p>
          <Trans>
            By using our services, you agree to comply with these terms
            regarding the use of AI-generated music.
          </Trans>
        </p>
        <h2>
          <Trans>Restrictions on Use of the Service</Trans>
        </h2>
        <p>
          &nbsp;
          <Trans>
            In addition to other restrictions in this Agreement, you agree not
            to:
          </Trans>
        </p>

        <ul>
          <li>
            <Trans>
              Make unauthorized copies of any content available through the
              Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Use any device or software to interfere with the proper
              functioning of the Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Attempt to decipher, decompile, disassemble, or reverse engineer
              the software or source code of the Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Delete or alter any material uploaded by Tad AI or others without
              authorization.
            </Trans>
          </li>
          <li>
            <Trans>
              Frame or link to any materials available on the Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Modify, deface, or bypass any approved software related to the
              Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Use Tad AI’s trademarks, logos, or content without permission.
            </Trans>
          </li>
          <li>
            <Trans>
              Access or tamper with non-public areas of the Service or Tad AI’s
              systems.
            </Trans>
          </li>
          <li>
            <Trans>Provide false personal information to Tad AI.</Trans>
          </li>
          <li>
            <Trans>
              Create a false identity or impersonate another person or entity.
            </Trans>
          </li>
          <li>
            <Trans>
              Create a new account after Tad AI has previously disabled your
              account without written consent.
            </Trans>
          </li>
          <li>
            <Trans>Solicit personal information from other Users.</Trans>
          </li>
          <li>
            <Trans>
              Restrict or discourage anyone from using the Service or disclosing
              third-party personal information without consent.
            </Trans>
          </li>
          <li>
            <Trans>
              Use the Service for any commercial or unauthorized purpose without
              Tad AI's express written consent.
            </Trans>
          </li>
          <li>
            <Trans>
              Gain unauthorized access to the Service or other Users’ accounts
              or personal information.
            </Trans>
          </li>
          <li>
            <Trans>
              Upload any virus, worm, spyware, or harmful code that may damage
              or disrupt the Service.
            </Trans>
          </li>
          <li>
            <Trans>
              Interfere with or disrupt the Service or networks connected to it.
            </Trans>
          </li>
          <li>
            <Trans>
              Upload or post any objectionable content or content that violates
              this Agreement.
            </Trans>
          </li>
          <li>
            <Trans>
              Violate any applicable laws or regulations or the terms of this
              Agreement.
            </Trans>
          </li>
        </ul>
        <h2>
          <Trans>Intellectual Property</Trans>
        </h2>
        <p>
          <Trans>
            By using our services, you acknowledge and respect the intellectual
            property rights of Tad AI and agree not to infringe upon them in any
            manner.
          </Trans>
        </p>
        <p>
          <Trans>
            All content on our Site, including but not limited to text,
            graphics, audio samples, logos, images, and software, is the
            exclusive property of Tad AI. This content is protected by
            copyright, trademark, and other intellectual property laws.
            Unauthorized use, reproduction, or distribution of any materials
            from our Site is strictly prohibited without prior written consent
            from Tad AI.&nbsp;
          </Trans>
        </p>
        <h2>
          <Trans>Term and Termination</Trans>
        </h2>
        <p>
          <Trans>
            The Term of this Agreement begins upon your first use of the Service
            and continues until terminated by either you or Tad AI.
          </Trans>
        </p>
        <p>
          <Trans>
            Tad AI reserves the right to restrict, suspend, or terminate this
            Agreement and your access to the Service at any time without prior
            notice or liability if you breach any provision of this Agreement or
            violate third-party copyright rights.
          </Trans>
        </p>
        <p>
          <Trans>
            Additionally, Tad AI retains the right to change, suspend, or
            discontinue any part of the Service at any time without prior notice
            or liability.
          </Trans>
        </p>
        <h2>
          <Trans>Indemnification</Trans>
        </h2>
        <p>
          <Trans>
            You agree to indemnify, defend, and hold harmless Tad AI, its
            affiliates, licensors, and service providers from any claims,
            losses, damages, liabilities, costs, or expenses, including
            reasonable attorney's fees, arising out of or related to your use of
            the Service, your violation of this Agreement, or your infringement
            of any intellectual property or other rights of any third party.
            This obligation will survive the termination of this Agreement and
            your use of the Service.
          </Trans>
        </p>
        <h2>
          <Trans>Disclaimer of Warranties</Trans>
        </h2>
        <p>
          <Trans>
            The Service is provided on an "as-is" and "as-available" basis,
            without any warranties of any kind, either express or implied. Tad
            AI disclaims all warranties, including but not limited to
            merchantability, fitness for a particular purpose, and
            non-infringement. Tad AI does not guarantee that the Service will be
            uninterrupted, secure, or free from errors, viruses, or other
            harmful components. You acknowledge that your use of the Service is
            at your own risk, and Tad AI will not be liable for any damages
            resulting from your use or inability to use the Service.
          </Trans>
        </p>
        <h2>
          <Trans>Refund Policy</Trans>
        </h2>
        <p>
          <SiteLink />{' '}
          <Trans>
            offers a 3-day refund policy for users who are not completely
            satisfied with our AI music generator. To be eligible for a refund,
            users must request it within <strong>3 days</strong> of purchase and
            must <strong>not</strong> have used more than 50 credits.
          </Trans>
        </p>
        <p>
          <Trans>
            Refunds will be processed using the same payment method as the
            original purchase and may take up to 10 business days to reflect in
            your account.
          </Trans>
        </p>
        <p>
          <Trans>
            Tad AI reserves the right to refuse refunds for users who violate
            our Terms of Service or engage in fraudulent activities. For more
            details on our refund policy and how to request a refund, please
            visit{' '}
          </Trans>
          <RefundPolicyLinks />.
        </p>
        <h2>
          <Trans>Contact Us</Trans>
        </h2>
        <p>
          <Trans>
            To reach out regarding your use of the Service, please see the
            contact information below:
          </Trans>
        </p>
        <p>
          <Trans>Company Name: {COMPANY_NAME}</Trans>
        </p>
        <p>
          <Trans>
            Address: 15 SCOTTS ROAD #03-12 15 SCOTTS SINGAPORE 228218
          </Trans>
        </p>
        <p>
          <Trans>Email:&nbsp;</Trans>
          <EmailLink />
        </p>
      </div>
    </div>
  )
}
const LayoutsPage = withLayouts(Page, [
  (props) => {
    const isApp = useIsAppQuery()

    return (
      <DefaultLayout
        {...props}
        minHScreen
        main={{
          className: 'bg-black',
        }}
        header={isApp ? null : undefined}
        footer={isApp ? null : undefined}
      />
    )
  },
  (props) => {
    const { t } = useLingui()
    return (
      <RootLayout
        {...props}
        seo={{
          title: t`Terms of Use | Tad AI`,
          description: t`Review the Terms of Use for Tad AI, outlining your rights and responsibilities while using our services. Stay informed about our policies and ensure a smooth experience.`,
        }}
      />
    )
  },
  (props) => {
    return <SiteSpecificContextLayout {...props} />
  },
  AntdLayout,
])

export default LayoutsPage
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (_, ctx, helpers) => {
  return {}
})
